import React from 'react'
import { Review } from 'hhsoig-design-system-core'
import css from './Inspect.module.css'
import {
  formatAddress,
  formatAttachedFiles,
  formatPersonName,
  formatReviewString,
  replacePhraseWithString
} from '../../utilities'
import { dateToString } from '../../utilities/methods/dateToString'
import { Divider } from '../divider'
import { PrintReview } from '../PrintReview'
import { ReviewIHSVictims } from '../ReviewIHSVictims'
import { ReviewParentGuardians } from '../ReviewParentGuardians'
import { ReviewSubjects } from '../ReviewSubjects'
import { ReviewWitnesses } from '../ReviewWitnesses'
import {
  addressLabel,
  cellPhoneLabel,
  employerLabel,
  employerPhoneLabel,
  homePhoneLabel,
  jobTitleLabel,
  nameLabel,
  personalEmailLabel,
  workEmailLabel,
  workPhoneLabel
} from '../../strings/labels'

// Return an array with Narrative and Attachments
export const complaintDescription = values => {
  return [
    {
      field_name: 'narrative',
      value: formatReviewString(values.narrative)
    },
    // Using 'evidence_description' as the field_name
    // so setting focus on an input field will work in OpDiv review.
    // Cannot set focus on 'documents' which is the actual name.
    {
      heading: 'Attached files',
      field_name: 'evidence_description',
      value: formatAttachedFiles(values.documents)
    },
    {
      heading: 'Evidence description',
      field_name: 'evidence_description',
      value: formatReviewString(values.evidence_description)
    }
  ]
}

export const InspectIHS = ({ goBackToPage, pages, values }) => {
  const contact_choices = require('../../choices/json/IHS_previous_contacts.json')
  const permission_choices = require('../../choices/json/disclosure_permission.json')
  const dateOccurredChoices = require('../../choices/json/IHS_date_occurred.json')
  const activityHasStarted =
    values.date_of_activity && values.date_of_activity.yearValue
  const activityHasEnded =
    values.activity_end_date && values.activity_end_date.yearValue

  const allegationDetails = [
    {
      heading: 'Do you know when this alleged abuse occurred?',
      field_name: 'know_when_this_occurred',
      value: formatReviewString(
        dateOccurredChoices[values.know_when_this_occurred]
      )
    }
  ]
  if (activityHasStarted) {
    allegationDetails.push({
      heading: 'Date abuse began',
      field_name: 'date_of_activity',
      value: formatReviewString(
        activityHasStarted && dateToString(values.date_of_activity, 'MDY')
      )
    })
    if (activityHasEnded) {
      allegationDetails.push({
        heading: 'Date abuse ended',
        field_name: 'activity_end_date',
        value: formatReviewString(
          activityHasEnded && dateToString(values.activity_end_date, 'MDY')
        )
      })
    }
  }

  const complaint_history = [
    {
      heading: 'Previously reported',
      field_name: 'previously_reported',
      value: formatReviewString(values.previously_reported)
    },
    {
      heading: 'Previously reported to',
      field_name: 'communication_contact',
      value: formatReviewString(
        replacePhraseWithString(
          [contact_choices[values.communication_contact]],
          values.communication_contact_other
        ).join(', ')
      )
    },
    {
      heading: 'Date previously reported',
      field_name: 'previously_reported_date',
      value: formatReviewString(
        values.previously_reported_date &&
          values.previously_reported_date.yearValue &&
          dateToString(values.previously_reported_date, 'MDY')
      )
    }
  ]

  const description_details = complaintDescription(values)

  const complainant_details = [
    {
      heading: 'Your consent to disclose',
      field_name: 'perm_to_disclose',
      value: permission_choices[values.perm_to_disclose]
    },
    {
      heading: nameLabel,
      field_name: 'complainant_first_name',
      value: formatReviewString(
        formatPersonName(
          values.complainant_first_name,
          values.complainant_middle_name,
          values.complainant_last_name,
          values.complainant_suffix
        )
      )
    }
  ]

  const complainant_contact_details = [
    {
      heading: homePhoneLabel,
      field_name: 'complainant_home_phone',
      value: formatReviewString(values.complainant_home_phone)
    },
    {
      heading: cellPhoneLabel,
      field_name: 'complainant_cell_phone',
      value: formatReviewString(values.complainant_cell_phone)
    },
    {
      heading: workPhoneLabel,
      field_name: 'complainant_work_phone',
      value: formatReviewString(values.complainant_work_phone)
    },
    {
      heading: personalEmailLabel,
      field_name: 'complainant_personal_email',
      value: formatReviewString(values.complainant_personal_email)
    },
    {
      heading: workEmailLabel,
      field_name: 'complainant_work_email',
      value: formatReviewString(values.complainant_work_email)
    },
    {
      heading: 'Your home address',
      field_name: 'complainant_address',
      value: formatReviewString(formatAddress(values.complainant_address))
    }
  ]

  const employer_information = [
    {
      heading: jobTitleLabel,
      field_name: 'complainant_job_title',
      value: formatReviewString(values.complainant_job_title)
    },
    {
      heading: employerLabel,
      field_name: 'complainant_employer',
      value: formatReviewString(values.complainant_employer)
    },
    {
      heading: addressLabel,
      field_name: 'complainant_work_address',
      value: formatReviewString(formatAddress(values.complainant_work_address))
    },
    {
      heading: employerPhoneLabel,
      field_name: 'complainant_employer_phone',
      value: formatReviewString(values.complainant_employer_phone)
    }
  ]

  const onEditClick = field_name =>
    goBackToPage(findPageIdOfField(field_name, pages))

  const findPageIdOfField = name => {
    let page
    page = pages.find(p => p.fields.some(f => f.name === name))
    if (!page) {
      // if page not found, search conditional fields
      page = pages.find(p =>
        p.fields.some(fld => {
          let conditionalFld
          if (fld.conditionals) {
            conditionalFld = fld.conditionals.find(c =>
              c.fields.some(f => f.name === name)
            )
            return conditionalFld ? true : false
          } else {
            return false
          }
        })
      )
    }
    return page ? page.id : null
  }

  // Create and return a <Review> tag for each detail object in 'details'.
  // The detail objects are defined above, and have 3 elements:
  // heading, field_name, value
  const iterateDetails = details => {
    return details.map((detail, i) => {
      const additionalProps = detail.omitEdit
        ? {}
        : {
            onEditClick: () => {
              goBackToPage(findPageIdOfField(detail.field_name))
            },
            editHref: 'javascript:void(0)'
          }
      return (
        <Review
          className={css.reviewContent}
          key={`${detail.field_name}_${i}`}
          heading={detail.heading}
          {...additionalProps}
        >
          {detail.value}
        </Review>
      )
    })
  }

  return (
    <div data-testid="review-complaint">
      <h1 className="form-pg-title">Your complaint summary</h1>
      <p className="ds-text">
        Before you submit, make sure you check your information and save or
        print a copy for your records.
      </p>
      <h2>Allegation details</h2>
      {iterateDetails(allegationDetails)}
      {values.previously_reported == 'Yes' && (
        <React.Fragment>
          <h2>Complaint history</h2>
          {iterateDetails(complaint_history)}
        </React.Fragment>
      )}

      <h2>Subject information</h2>
      <ReviewSubjects values={values} onClick={onEditClick} />
      <h2>Victim information</h2>
      <ReviewIHSVictims values={values} onClick={onEditClick} />
      {values.know_parent_guardian == 'Yes' && (
        <React.Fragment>
          <h2>Parent/Guardian information</h2>
          <ReviewParentGuardians values={values} onClick={onEditClick} />
        </React.Fragment>
      )}
      {values.witness_corroboration == 'Yes' && (
        <React.Fragment>
          <h2>Witness information</h2>
          <ReviewWitnesses values={values} onClick={onEditClick} />
        </React.Fragment>
      )}
      <h2>Your narrative</h2>
      <h3>Your description of events</h3>
      {iterateDetails(description_details)}
      <h2>Your information</h2>
      {values.perm_to_disclose != 'Anonymous' && (
        <React.Fragment>
          {iterateDetails(complainant_details)}
          <h3>Your contact details</h3>
          {iterateDetails(complainant_contact_details)}
          <h3>Your employment information</h3>
          {iterateDetails(employer_information)}
        </React.Fragment>
      )}
      <Divider />
      <PrintReview className={css.printReview} />
      <Divider />
    </div>
  )
}
